import { createContext, type Ref } from 'react';
import type { LabelProps as AriaLabelProps } from 'react-aria-components/Label';
import { Label as AriaLabel } from 'react-aria-components/Label';
import type { ContextValue } from 'react-aria-components/slots';
import { cva, cx } from 'class-variance-authority';

import { useLPContextProps } from './utils';

import styles from './styles/Label.module.css';

const labelStyles = cva(styles.label, {
	variants: {
		size: {
			small: styles.small,
			medium: styles.medium,
		},
	},
	defaultVariants: {
		size: 'medium',
	},
});

interface LabelProps extends Omit<AriaLabelProps, 'className'> {
	ref?: Ref<HTMLLabelElement>;
	size?: 'small' | 'medium';
	/** Maximum number of lines to display. Overflowing text will be truncated with an ellipsis. */
	maxLines?: number;
	/** Optional CSS class name */
	className?: AriaLabelProps['className'];
}

const LabelContext = createContext<ContextValue<LabelProps, HTMLLabelElement>>(null);

/**
 * A generic Label component for form labels.
 *
 * For body text, use `Text`. For headings, use `Heading`
 *
 * A label whose content is plain text renders a required asterisk automatically when its parent
 * field sets `isRequired`. A label that composes element children is assumed to own its content,
 * including any required marker, and never receives the automatic asterisk.
 *
 * Built on top of [React Aria `Label` component](https://react-spectrum.adobe.com/react-spectrum/Label.html#label).
 */
const Label = ({ ref, maxLines, style, size, ...props }: LabelProps) => {
	const [contextProps, contextRef] = useLPContextProps(props, ref, LabelContext);
	const { className } = contextProps;

	return (
		<AriaLabel
			{...contextProps}
			ref={contextRef}
			className={cx(labelStyles({ size }), maxLines && styles.truncate, className, props.className)}
			style={{
				...style,
				...(maxLines && {
					WebkitLineClamp: maxLines,
				}),
			}}
		/>
	);
};

Label.displayName = 'Label';

export { Label, LabelContext, labelStyles };
export type { LabelProps };
